/**
 * `/jobs` -- the job monitor, the audited actions, and the metrics.
 *
 * DECIDES NOTHING ITSELF, and APPLIES nothing itself either. Which actions
 * exist and how each is gated, what a 409 means, how a DATEV 400 differs from a
 * DATEV 502, when the table may poll, which of six shapes a `stats_json` is,
 * and which of four states a metrics bundle is in -- all of it lives in
 * lib/jobsView.ts, lib/jobStats.ts, lib/metrics.ts and lib/jobDisplay.ts, where
 * a Node test can hold the collaborators (the STANDING RULE from the Task 7-12
 * reviews). What is left here is JSX and component state.
 *
 * ── THE THREE NUMBERS THIS VIEW MUST NOT GET WRONG ──────────────────────────
 *
 *  1. "DAUER" IS `publicJob.durationMs`, NEVER `stats.durationMs`. They are
 *     legitimately different numbers and the merged code says which one belongs
 *     here (adminRoutes.ts:233-239, review N-2): `durationMs` is the wall clock
 *     for the row's whole life -- for a background action it also covers the
 *     waitUntil scheduling gap -- while `stats.durationMs` times the job BODY
 *     alone. `formatDuration` structurally cannot see the stats (see
 *     lib/jobDisplay.ts's docblock), and the Details column shows the body's
 *     number labelled as what it is.
 *  2. "LETZTE AUSFÜHRUNG JE TYP" IS `data.latest`, NEVER DERIVED FROM
 *     `data.jobs`. The server answers both halves from ONE request precisely so
 *     the summary is not a client-side guess (D6, adminRoutes.ts:1696-1700):
 *     `latest` is one row per type regardless of how far down the page it fell,
 *     and a "newest of what happens to be on page 1" derivation would quietly
 *     stop naming a type as soon as it dropped off the page.
 *  3. A SERIES IS LABELLED WHAT IT COUNTS. In particular the Sheets chart says
 *     "Leseaufrufe" and never "Kontingent" -- see lib/metrics.ts's
 *     `FORBIDDEN_LABEL_WORDS`.
 *
 * TWO PANELS ON ONE ROUTE, ADDRESSED BY THE FRAGMENT (lib/router.ts's
 * `parseJobsTab`, parsed there and never here). The monitor and the actions
 * share a tab on purpose: the buttons create the rows the table shows, and a
 * `409 job_running` scrolls to the row it collided with, which it can only do
 * if that row is mounted.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMe } from '../Boot';
import { useRoute } from '../Router';
import { ADMIN_API } from '../lib/api';
import { t } from '../lib/i18n';
import { formatDuration, formatTimestamp, jobStatusPill, truncateError } from '../lib/jobDisplay';
import {
  ACTION_GROUPS, DATEV_PREVIEW_ACTION, METRIC_RANGES, actionGroupLabel, actionLabel,
  actionOutcomeMessage, actionOutcomeTone, datevOutcomeMessage, datevOutcomeTone, downloadBlob,
  focusJob, metricRangeLabel, metricsViewState, rowDomId, runAdminAction, runDatevExport,
  visibleActions,
  type ActionGroup, type ActionName, type ActionOutcome, type DatevOutcome,
} from '../lib/jobsView';
import {
  METRIC_SERIES, seriesLabel, seriesNote, seriesPlan, seriesRowsOf, type SeriesPlan,
} from '../lib/metrics';
import {
  isPlainLeftClick, jobsTabHref, masterDataTabHref, parseJobsTab, type JobsTab,
} from '../lib/router';
import { jobsQueryOptions, metricsQueryOptions } from '../queries';
import { cn } from '../lib/utils';
import { ErrorNotice } from '../components/ErrorNotice';
import { PageBody, PageHeader, TabNav } from '../components/ui/page';
import { JobLine } from '../components/JobLine';
import { JobStatsCell } from '../components/JobStatsCell';
import { Spinner } from '../components/Spinner';
import { StatusPill } from '../components/StatusPill';
import { BarChart } from '../components/charts/BarChart';
import { LineChart } from '../components/charts/LineChart';
import { RatioBar } from '../components/charts/RatioBar';
import { isAllowed, type CapabilityCtx } from '../lib/capabilities';
import type { MetricRange, MetricSeriesName, MetricsBundle, PublicJob } from '../lib/types';

// ── the monitor ─────────────────────────────────────────────────────────────

/**
 * ONE ROW OF THE PAGED MONITOR, and deliberately NOT `<JobLine>`.
 *
 * Task 8 shipped `<JobLine>` claiming this view would reuse it "with no
 * rewrite, only an import"; the Task 8 review disproved that and the claim was
 * corrected. `JobLine` is an `<li>` with no Start time and no Details slot,
 * built for the Start card's compact list -- this table needs six columns
 * including `<JobStatsCell>`, and forcing one component to be both would give
 * each surface the other's shape. `JobLine` IS still the right renderer for the
 * "letzte Ausführung je Typ" summary above, which is a compact list, and that
 * is where it is used.
 *
 * The DOM id is `rowDomId(job.id)` so `focusJob` can scroll to the run a
 * `409 job_running` names.
 */
function JobMonitorRow({ job }: { job: PublicJob }) {
  const pill = jobStatusPill(job.status);
  const shortError = truncateError(job.error);
  return (
    <tr id={rowDomId(job.id)} data-job-type={job.type} data-job-status={job.status}>
      <td className="job-cell-type">{job.type}</td>
      <td className="job-cell-status"><StatusPill kind={pill.kind} label={pill.label} /></td>
      <td className="job-cell-started">{formatTimestamp(job.startedAt)}</td>
      {/* THE WALL CLOCK. See this file's docblock, point 1. */}
      <td className="job-cell-duration">{formatDuration(job.durationMs)}</td>
      <td className="job-cell-stats"><JobStatsCell stats={job.stats} /></td>
      <td className="job-cell-error" title={job.error ?? undefined}>{shortError ?? ''}</td>
    </tr>
  );
}

function MonitorTable({ jobs }: { jobs: PublicJob[] }) {
  if (jobs.length === 0) return <p className="jobs-empty">{t('jobs.monitor.empty')}</p>;
  return (
    <table className="jobs-table">
      <thead>
        <tr>
          <th scope="col">{t('jobs.col.type')}</th>
          <th scope="col">{t('jobs.col.status')}</th>
          <th scope="col">{t('jobs.col.started')}</th>
          <th scope="col">{t('jobs.col.duration')}</th>
          <th scope="col">{t('jobs.col.details')}</th>
          <th scope="col">{t('jobs.col.error')}</th>
        </tr>
      </thead>
      <tbody>
        {jobs.map((job) => <JobMonitorRow key={job.id} job={job} />)}
      </tbody>
    </table>
  );
}

/** `data.latest`, RENDERED AS GIVEN. See this file's docblock, point 2. */
function LatestPerType({ latest, nowMs }: { latest: PublicJob[]; nowMs: number }) {
  return (
    <section className="jobs-latest">
      <h2>{t('jobs.latest.title')}</h2>
      {latest.length === 0
        ? <p className="jobs-empty">{t('jobs.latest.empty')}</p>
        : (
          <ul className="job-lines">
            {latest.map((job) => <JobLine key={job.id} job={job} nowMs={nowMs} />)}
          </ul>
        )}
    </section>
  );
}

// ── the actions ─────────────────────────────────────────────────────────────

/**
 * A `bug` outcome is not an error toast: a reachable disabled action means
 * capabilities.ts and the server disagree, which is a deployment fault and is
 * said in those words rather than as "der Vorgang ist fehlgeschlagen".
 *
 * `role="alert"` (assertive) for BOTH the failure and the bug tones, and only
 * `status` (polite) for success. A screen-reader user must not learn from a
 * polite queue that the action they just pressed failed, or that a button
 * existed which should not have existed at all.
 */
function OutcomeLine({ message, tone }: { message: string; tone: 'ok' | 'error' | 'bug' }) {
  return (
    <p className="jobs-outcome" data-tone={tone} role={tone === 'ok' ? 'status' : 'alert'}>
      {message}
    </p>
  );
}

function ActionGroupPanel({ group, ctx, pending, onRun }: {
  group: ActionGroup;
  ctx: CapabilityCtx;
  pending: ActionName | null;
  onRun: (name: ActionName) => void;
}) {
  const names = visibleActions(group, ctx);
  if (names.length === 0) return null;
  return (
    <div className="jobs-action-group" data-group={group}>
      <h3>{actionGroupLabel(group)}</h3>
      {names.map((name) => (
        <button
          key={name}
          type="button"
          data-action={name}
          disabled={pending !== null}
          onClick={() => onRun(name)}
        >
          {actionLabel(name)}
        </button>
      ))}
    </div>
  );
}

// ── DATEV ───────────────────────────────────────────────────────────────────

/**
 * The registry's ONE exception: it returns a FILE, writes no jobs row, and its
 * failures deliberately bypass `ApiError` (`adminFetchRaw`). Everything about
 * that -- the local-time -> UTC conversion, the filename, the 400/502 split --
 * is in lib/jobsView.ts; this is the form.
 */
function DatevPanel({ onExport, pending, outcome }: {
  onExport: (since: string, until: string) => void;
  pending: boolean;
  outcome: DatevOutcome | null;
}) {
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  return (
    <section className="jobs-datev" data-action={DATEV_PREVIEW_ACTION}>
      <h3>{t('jobs.datev.title')}</h3>
      <p>{t('jobs.datev.body')}</p>
      <label>
        {t('jobs.datev.since')}
        <input
          type="datetime-local"
          value={since}
          onChange={(event) => setSince(event.target.value)}
        />
      </label>
      <label>
        {t('jobs.datev.until')}
        <input
          type="datetime-local"
          value={until}
          onChange={(event) => setUntil(event.target.value)}
        />
      </label>
      <button type="button" disabled={pending} onClick={() => onExport(since, until)}>
        {t('jobs.datev.submit')}
      </button>

      {outcome === null ? null : (
        <>
          <OutcomeLine message={datevOutcomeMessage(outcome)} tone={datevOutcomeTone(outcome)} />
          {/* The two failures the server splits get the two different repairs it
              splits them FOR: a config problem is fixed in Stammdaten, a
              transient upstream failure is fixed by trying again. Merging them
              would send the operator to the wrong one every second time. */}
          {outcome.kind === 'configMissing'
            ? <a href={masterDataTabHref('settings')}>{t('jobs.datev.configLink')}</a>
            : null}
          {/* The retry re-submits THE FIELDS AS THEY STAND, which is what the
              operator sees in front of them -- not a range parked in a parent's
              state, which can only ever disagree with them. */}
          {outcome.kind === 'upstream'
            ? <button type="button" onClick={() => onExport(since, until)}>{t('blocking.retry')}</button>
            : null}
        </>
      )}
    </section>
  );
}

// ── the metrics ─────────────────────────────────────────────────────────────

/**
 * ONE GENERIC RENDERER FOR ALL SIX SERIES (Task 13 review M1).
 *
 * The first round put ~115 lines of "which number goes in which column" here,
 * and eight mutations survived in it. That mapping now lives in
 * lib/metrics.ts's `SERIES_PLAN`, a `Record<MetricSeriesName, …>` table that a
 * Node test drives with an expected plan per series -- so a jobs table showing
 * `1 - errorRate`, or p50 and p95 swapped, or a ratio bar fed `hit`/`miss` the
 * wrong way round, are no longer things that can be written HERE at all.
 *
 * What is left is a switch on `plan.kind` that formats nothing.
 */
function SeriesRows({ plan }: { plan: SeriesPlan }) {
  return (
    <>
      {plan.kind === 'line' ? <LineChart plan={plan} /> : null}
      {plan.kind === 'bar' ? <BarChart plan={plan} /> : null}
      {plan.kind === 'ratio' ? <RatioBar plan={plan} /> : null}
      {plan.kind === 'table' ? (
        <table className="metric-table">
          <thead>
            <tr>{plan.columns.map((column) => <th key={column} scope="col">{column}</th>)}</tr>
          </thead>
          <tbody>
            {plan.rows.map((row) => (
              <tr key={row.key} data-row={row.key}>
                {row.cells.map((cell, i) => <td key={`${row.key}-${i}`}>{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      <SeriesFooter count={plan.count} dropped={plan.dropped} />
    </>
  );
}

/** A dropped row is SAID, never swallowed -- see lib/metrics.ts's docblock for
 *  why a malformed row must not silently become a zero. */
function SeriesFooter({ count, dropped }: { count: number; dropped: number }) {
  return (
    <>
      {count === 0 && dropped === 0
        ? <p className="metric-empty">{t('jobs.metrics.seriesEmpty')}</p>
        : null}
      {dropped > 0
        ? <p className="metric-dropped" role="alert">{t('jobs.metrics.dropped', { dropped })}</p>
        : null}
    </>
  );
}

function SeriesPanel({ name, bundle }: { name: MetricSeriesName; bundle: MetricsBundle }) {
  const rows = seriesRowsOf(bundle, name);
  const note = seriesNote(name);
  return (
    <section className="metric-panel" data-series={name}>
      <h3>{seriesLabel(name)}</h3>
      {note === null ? null : <p className="metric-note">{note}</p>}
      {/* `null` IS NOT `[]`. "We could not ask" replaces the chart entirely --
          a partial bundle must not look complete. */}
      {rows === null
        ? <p className="metric-unavailable" role="alert">{t('jobs.metrics.seriesFailed')}</p>
        : <SeriesRows plan={seriesPlan(name, rows)} />}
    </section>
  );
}

function MetricsPanel({ range, onRange }: { range: MetricRange; onRange: (range: MetricRange) => void }) {
  const metrics = useQuery(metricsQueryOptions(range));
  const state = metricsViewState(metrics);

  return (
    <section className="jobs-metrics">
      <h2>{t('jobs.metrics.title')}</h2>
      {/* A segmented control: one bordered group, the pressed segment filled.
          `aria-pressed` still carries the state -- the fill is the visual half
          of the same fact, never a replacement for it. */}
      <div
        className="jobs-metrics-ranges mb-4 inline-flex rounded-field border border-line bg-surface-2 p-0.5"
        role="group"
        aria-label={t('jobs.metrics.rangeLabel')}
      >
        {METRIC_RANGES.map((candidate) => (
          <button
            key={candidate}
            type="button"
            data-range={candidate}
            aria-pressed={candidate === range}
            onClick={() => onRange(candidate)}
            className={cn(
              'h-7 rounded-[0.3rem] border-0 px-3 text-xs font-medium transition-colors',
              candidate === range
                ? 'bg-surface text-text shadow-card'
                : 'bg-transparent text-muted hover:bg-transparent hover:text-text',
            )}
          >
            {metricRangeLabel(candidate)}
          </button>
        ))}
      </div>

      {state.kind === 'loading' ? <Spinner /> : null}
      {/* 503 metrics_unconfigured: THE PANEL ALONE degrades (spec 4.7, risk
          A3). It is a named environment state, not an error toast. */}
      {state.kind === 'unconfigured' ? (
        <div className="metrics-unconfigured" role="status">
          <h3>{t('jobs.metrics.unconfigured.title')}</h3>
          <p>{t('jobs.metrics.unconfigured.body')}</p>
        </div>
      ) : null}
      {state.kind === 'error' ? <ErrorNotice error={metrics.error} /> : null}
      {state.kind === 'empty' ? <p className="metrics-empty">{t('jobs.metrics.empty')}</p> : null}
      {state.kind === 'ready' ? (
        <>
          {state.partial ? <p className="metrics-partial" role="alert">{t('jobs.metrics.partial')}</p> : null}
          {METRIC_SERIES.map((name) => <SeriesPanel key={name} name={name} bundle={state.bundle} />)}
        </>
      ) : null}
    </section>
  );
}

// ── the monitor + actions panel ─────────────────────────────────────────────

function JobsPanel({ ctx }: { ctx: CapabilityCtx }) {
  const queryClient = useQueryClient();
  const jobs = useQuery(jobsQueryOptions());

  const actionGate = useRef<string | null>(null);
  const datevGate = useRef<string | null>(null);
  const [pending, setPending] = useState<ActionName | null>(null);
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  const [datevPending, setDatevPending] = useState(false);
  const [datevOutcome, setDatevOutcome] = useState<DatevOutcome | null>(null);

  /**
   * The 409's OWN jobId. `focusJob` refuses a null one and refuses a row that
   * is not on the page, so this never scrolls to "some running row" -- which
   * would name a different job as the blocker than the one the server named.
   */
  useEffect(() => {
    if (outcome?.kind !== 'jobRunning') return;
    focusJob(outcome.jobId, { find: (domId) => document.getElementById(domId) });
  }, [outcome]);

  function handleRun(name: ActionName) {
    void runAdminAction(name, {
      gate: actionGate,
      queryClient,
      runAction: ADMIN_API.runAction,
      confirm: (message) => window.confirm(message),
      setPending,
      setOutcome,
    });
  }

  function handleDatev(since: string, until: string) {
    void runDatevExport(since, until, {
      gate: datevGate,
      queryClient,
      datevPreview: ADMIN_API.datevPreview,
      download: downloadBlob,
      setPending: setDatevPending,
      setOutcome: setDatevOutcome,
    });
  }

  return (
    <>
      <section className="jobs-actions">
        <h2>{t('jobs.actions.title')}</h2>
        {outcome === null
          ? null
          : <OutcomeLine message={actionOutcomeMessage(outcome)} tone={actionOutcomeTone(outcome)} />}
        {ACTION_GROUPS.map((group) => (
          <ActionGroupPanel key={group} group={group} ctx={ctx} pending={pending} onRun={handleRun} />
        ))}
        {/* ITS OWN GATE, STATED (Task 13 review M4). Round 1 rendered this on
            `visibleActions('ebay', ctx).length === 0` -- a SIDE EFFECT of the
            eBay group being empty, which would have moved DATEV silently the
            next time eBay gating changed. `datev` is now a surface of its own in
            capabilities.ts: feature-gated on `ebay` exactly as the server gates
            it (adminRoutes.ts:1513-1516) and NOT paused by billing, because the
            server reads no `tenant.status` on that path and the client must not
            invent a refusal the platform does not make.
            It is also deliberately NOT behind the actions' busy gate: the server
            keeps this export outside the per-tenant job lock, so a running sync
            must not grey out an accounting export. */}
        {isAllowed('datev', ctx) ? (
          <DatevPanel onExport={handleDatev} pending={datevPending} outcome={datevOutcome} />
        ) : null}
      </section>

      {jobs.isPending ? <Spinner /> : null}
      {jobs.isError ? (
        <div className="jobs-error">
          <ErrorNotice error={jobs.error} />
          <button type="button" onClick={() => void jobs.refetch()}>{t('blocking.retry')}</button>
        </div>
      ) : null}
      {jobs.isSuccess ? (
        <>
          <LatestPerType latest={jobs.data.latest ?? []} nowMs={Date.now()} />
          <section className="jobs-monitor">
            <h2>{t('jobs.monitor.title')}</h2>
            <MonitorTable jobs={jobs.data.jobs ?? []} />
          </section>
        </>
      ) : null}
    </>
  );
}

// ── the view ────────────────────────────────────────────────────────────────

export function Jobs() {
  const me = useMe();
  const { hash, navigate } = useRoute();
  const [range, setRange] = useState<MetricRange>('24h');

  // Boot resolves `me` before any view mounts, so this is a cache read.
  if (!me.isSuccess) return <Spinner />;

  // The fragment is parsed by the pure parser in lib/router.ts, never here.
  const tab: JobsTab = parseJobsTab(hash);
  const ctx: CapabilityCtx = {
    role: me.data.role,
    status: me.data.tenant.status,
    features: me.data.features,
  };

  return (
    <section className="jobs-view">
      <PageBody>
        <PageHeader title={t('nav.jobs')} />
        <TabNav
          className="jobs-tabs"
          label={t('jobs.tabs.label')}
          activeId={tab}
          items={[
            { id: 'jobs' as const, label: t('jobs.tab.jobs'), href: jobsTabHref('jobs') },
            { id: 'metrics' as const, label: t('jobs.tab.metrics'), href: jobsTabHref('metrics') },
          ]}
          onTabClick={(event, href) => {
            // THE SHARED PREDICATE (final review m-1). This call site used to
            // check meta and ctrl only, so on `/jobs` -- and nowhere else in
            // the SPA -- shift-click (new window) and alt-click (download)
            // were swallowed by the preventDefault below.
            if (!isPlainLeftClick(event)) return;
            event.preventDefault();
            navigate(href);
          }}
        />

        {tab === 'jobs' ? <JobsPanel ctx={ctx} /> : <MetricsPanel range={range} onRange={setRange} />}
      </PageBody>
    </section>
  );
}
